/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './ad-unit-matching-label-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {SearchApiService} from '../../common/controller/search-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-ad-unit-matching-label-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdUnitMatchingLabelTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.adUnitMatchingLabel;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputted: string[] = [];
  searchTerm = '';
  private readonly listState = signal<any>({searchResults: [], totalHits: 0, pageStart: 0, pageEnd: 0});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  listLoaded = false;
  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolved: true,
    changePage: () => this.search()
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summary = _.flatten(_.map(this.model || [], (item) => item.matchingLabels)).join(', ');
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.adUnitMatchingLabel,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(private readonly searchApi: SearchApiService) {
  }

  ngOnInit(): void {
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
    this.tabController.register(ADN_TARGETING_TYPES.adUnitMatchingLabel.widget, () => {
      if (this.listLoaded) {
        return;
      }
      this.search(true);
    }, copyTemplate);
  }

  remove(value: string): void {
    _.remove(this.inputted, (input) => input === value);
  }

  search(newSearch?: boolean): void {
    this.searchMeta.resolved = false;
    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    this.searchApi.search({
      q: this.searchTerm || '',
      types: 'MatchingLabel',
      objectState: 'ACTIVE',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    }).subscribe((page) => {
        this.list = normalizeTargetingSearchPage(page, {
          pageSize: this.searchMeta.pageSize,
          currentPage: this.searchMeta.currentPage
        });
        this.listLoaded = true;
        this.searchMeta.resolved = true;
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  isSelected(label: string): boolean {
    return this.inputted.indexOf(label) > -1;
  }

  toggleLabel(label: string): void {
    const idx = this.inputted.indexOf(label);
    if (idx > -1) {
      this.inputted.splice(idx, 1);
    } else {
      this.inputted.push(label);
    }
  }

  private initFromModel(): void {
    const inputted: string[] = [];
    _.forEach(this.model || [], (entryObj) => {
      inputted.push(entryObj.matchingLabels[0]);
    });
    this.inputted = inputted;
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.inputted, (v) => {
      this.model.push({matchingLabels: [v]});
    });
    this.initFromModel();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initFromModel();
  }
}
